/**
 * Asymmetric-margin proposal card (the runtime HITL gate for
 * `apply_asymmetric_margin`). Pure presentational: it shows the RESOLVED target
 * (name + number + current volume), EACH of the six patient anatomical
 * directions with its OWN signed value in CENTIMETRES, the NAMED patient (LPS)
 * coordinate frame, the previewed volume change, and the new structure name;
 * nothing mutates until the clinician presses Apply. The coordinate frame is
 * this slice's dominant hazard — every direction is shown explicitly with no
 * inferred symmetry, in a labelled definition list (each direction is a
 * label/value pair, tabular figures, the frame named) so a swapped or
 * wrong-signed axis is visible before approval. The card frame, status chip,
 * hover preview, and Apply/Reject gate live in {@link ProposalCardShell}; this
 * component supplies only the body.
 */
import { Box, Typography } from '@mui/material';
import type { RoundedDirection } from '~/agent';
import {
    formatCmTrimmed,
    formatSignedCm,
    formatSignedCm3,
} from '~/utils/units';
import GridRoundingNote from './GridRoundingNote';
import ProposalCardShell, {
    PreviewedVolumeLine,
    ProposalSummaryBox,
    TargetLine,
    type ProposalStatus,
} from './ProposalCardShell';

export interface AsymmetricMarginProposalCardProps {
    readonly targetName: string;
    readonly targetRoiNumber: number;
    readonly currentVolumeCm3: number;
    /** Signed cm per patient direction: + = expand, − = contract, 0 = none. */
    readonly superiorCm: number;
    readonly inferiorCm: number;
    readonly anteriorCm: number;
    readonly posteriorCm: number;
    readonly leftCm: number;
    readonly rightCm: number;
    /** Directions rounded to the grid, set only when any were finer. */
    readonly roundedDirections?: ReadonlyArray<RoundedDirection>;
    readonly newRoi: string;
    readonly volumeDeltaCm3: number;
    readonly status: ProposalStatus;
    readonly failureReason?: string;
    readonly onApply: () => void;
    readonly onReject: () => void;
    readonly onPreviewEnter: () => void;
    readonly onPreviewLeave: () => void;
}

export default function AsymmetricMarginProposalCard({
    targetName,
    targetRoiNumber,
    currentVolumeCm3,
    superiorCm,
    inferiorCm,
    anteriorCm,
    posteriorCm,
    leftCm,
    rightCm,
    roundedDirections,
    newRoi,
    volumeDeltaCm3,
    status,
    failureReason,
    onApply,
    onReject,
    onPreviewEnter,
    onPreviewLeave,
}: AsymmetricMarginProposalCardProps) {
    // The six explicit directions, in opposing pairs as the clinician knows
    // them from the manual asymmetric controls. Each is an independent
    // label/value pair — no direction is inferred from another.
    const directions: ReadonlyArray<readonly [string, number]> = [
        ['Superior', superiorCm],
        ['Inferior', inferiorCm],
        ['Anterior', anteriorCm],
        ['Posterior', posteriorCm],
        ['Left', leftCm],
        ['Right', rightCm],
    ];

    return (
        <ProposalCardShell
            title="Asymmetric margin"
            ariaLabel="Asymmetric margin proposal"
            status={status}
            failureReason={failureReason}
            onApply={onApply}
            onReject={onReject}
            onPreviewEnter={onPreviewEnter}
            onPreviewLeave={onPreviewLeave}
        >
            <TargetLine
                targetName={targetName}
                targetRoiNumber={targetRoiNumber}
                currentVolumeCm3={currentVolumeCm3}
            />

            <ProposalSummaryBox>
                asymmetric margin on “{targetName}”{' → '}
                <strong>{newRoi}</strong>
            </ProposalSummaryBox>

            {/* Each direction is an explicit label/value pair in a definition
                list; the patient frame is NAMED so a swapped axis is
                unambiguous. */}
            <Box>
                <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                    mb={0.25}
                >
                    Per-direction margin · patient frame (LPS)
                </Typography>
                <Box
                    component="dl"
                    sx={{
                        m: 0,
                        display: 'grid',
                        gridTemplateColumns: '1fr auto 1fr auto',
                        columnGap: 1,
                        rowGap: 0.25,
                        fontSize: '0.75rem',
                        fontVariantNumeric: 'tabular-nums',
                    }}
                >
                    {directions.map(([label, value]) => (
                        <Box key={label} sx={{ display: 'contents' }}>
                            <Box
                                component="dt"
                                sx={{ color: 'text.secondary' }}
                            >
                                {label}
                            </Box>
                            <Box
                                component="dd"
                                sx={{
                                    m: 0,
                                    textAlign: 'right',
                                    fontWeight: 600,
                                }}
                            >
                                {formatSignedCm(value)}
                            </Box>
                        </Box>
                    ))}
                </Box>
            </Box>

            {roundedDirections && roundedDirections.length > 0 && (
                <GridRoundingNote>
                    Rounded to the 0.1 mm grid:{' '}
                    {roundedDirections
                        .map(
                            (d) =>
                                `${d.label} ${formatCmTrimmed(d.requestedCm)} cm`
                        )
                        .join(', ')}
                    .
                </GridRoundingNote>
            )}

            <PreviewedVolumeLine
                label="Previewed volume change"
                value={formatSignedCm3(volumeDeltaCm3)}
            />
        </ProposalCardShell>
    );
}
